import { atom, getDefaultStore, useAtomValue, useSetAtom } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FinalizedAttachment } from '../types'

export const MAX_QUEUED_MESSAGES_PER_CHAT = 3

export type QueuedMessage = {
  localId: string
  messageId: string | null
  prompt: string
  // Populated after `finalizeAttachments` resolves on submit; used by the
  // bubble to show a small paperclip indicator. Bubble is created with []
  // and stamped via `setQueuedAttachments` once uploads complete, so the
  // indicator may pop in ~50-500ms after the bubble first appears.
  attachedContext: Array<FinalizedAttachment>
  cancelling: boolean
}

type QueuedMessagesMap = Record<string, Array<QueuedMessage>>

const queuedMessagesAtom = atom<QueuedMessagesMap>({})

// Chats whose most recent assistant turn just finished while messages are
// still queued — used by QueuedMessagesPanel to shimmer the next-to-drain
// bubble until the backend starts the next turn.
const chatAwaitingNextDrainAtom = atom<Set<string>>(new Set<string>())

const emptyList: Array<QueuedMessage> = []

export const getQueueForChat = (chatId: string | undefined): Array<QueuedMessage> => {
  if (!chatId) return emptyList
  return getDefaultStore().get(queuedMessagesAtom)[chatId] ?? emptyList
}

export const enqueueMessage = (chatId: string, item: QueuedMessage): boolean => {
  let accepted = false
  getDefaultStore().set(queuedMessagesAtom, (prev) => {
    const list = prev[chatId] ?? emptyList
    if (list.length >= MAX_QUEUED_MESSAGES_PER_CHAT) return prev
    accepted = true
    return { ...prev, [chatId]: [...list, item] }
  })
  return accepted
}

export const hydrateQueuedMessage = (chatId: string, localId: string, messageId: string): void => {
  getDefaultStore().set(queuedMessagesAtom, (prev) => {
    const list = prev[chatId]
    if (!list) return prev
    const idx = list.findIndex((m) => m.localId === localId)
    if (idx === -1) return prev
    const next = [...list]
    next[idx] = { ...next[idx], messageId }
    return { ...prev, [chatId]: next }
  })
}

export const setQueuedAttachments = (
  chatId: string,
  localId: string,
  attachedContext: Array<FinalizedAttachment>,
): void => {
  getDefaultStore().set(queuedMessagesAtom, (prev) => {
    const list = prev[chatId]
    if (!list) return prev
    const idx = list.findIndex((m) => m.localId === localId)
    if (idx === -1) return prev
    const next = [...list]
    next[idx] = { ...next[idx], attachedContext }
    return { ...prev, [chatId]: next }
  })
}

export const removeQueuedMessage = (chatId: string, localId: string): boolean => {
  let queueEmpty = false
  let removed = false
  getDefaultStore().set(queuedMessagesAtom, (prev) => {
    const list = prev[chatId]
    if (!list) return prev
    const next = list.filter((m) => m.localId !== localId)
    if (next.length === list.length) return prev
    removed = true
    if (next.length === 0) {
      queueEmpty = true
      const { [chatId]: _, ...rest } = prev
      return rest
    }
    return { ...prev, [chatId]: next }
  })
  // Awaiting-drain is meaningful only while items remain. Drop it the moment
  // the queue empties (cancel, last drain, error rollback) so future turns on
  // this chat don't inherit a stuck flag that would wedge the loader and the
  // most-recent message actions.
  if (queueEmpty) {
    clearChatAwaitingNextDrain(chatId)
  }
  return removed
}

export const isQueuedMessagePresent = (chatId: string, localId: string): boolean => {
  const list = getDefaultStore().get(queuedMessagesAtom)[chatId]
  return !!list && list.some((m) => m.localId === localId)
}

export const useQueuedMessages = (chatId: string | undefined): Array<QueuedMessage> => {
  const selector = useCallback(
    (state: QueuedMessagesMap) => (chatId ? (state[chatId] ?? emptyList) : emptyList),
    [chatId],
  )
  const selectedAtom = useMemo(() => selectAtom(queuedMessagesAtom, selector), [selector])
  return useAtomValue(selectedAtom)
}

// Boolean-selector flavor for consumers that only care whether the queue
// has any items, not its contents. Avoids re-rendering on per-item mutations
// (attachment stamping, cancelling toggling, hydration) that don't flip
// emptiness.
export const useIsChatQueueNonEmpty = (chatId: string | undefined): boolean => {
  const selector = useCallback(
    (state: QueuedMessagesMap) => (chatId ? (state[chatId]?.length ?? 0) > 0 : false),
    [chatId],
  )
  const selectedAtom = useMemo(() => selectAtom(queuedMessagesAtom, selector), [selector])
  return useAtomValue(selectedAtom)
}

// Delay between a turn's held-open SSE stream closing and the gradient glow
// lighting up the next queued bubble. Gives the backend a chance to start
// draining the next turn (whose `metadata` cancels the pending mark) before
// we commit to showing the glow — so back-to-back drains don't briefly flash
// it.
const MARK_AWAITING_DELAY_MS = 350

const pendingMarkTimers = new Map<string, ReturnType<typeof setTimeout>>()

export const markChatAwaitingNextDrain = (chatId: string): void => {
  if (!chatId) return
  const existing = pendingMarkTimers.get(chatId)
  if (existing) clearTimeout(existing)
  const timer = setTimeout(() => {
    pendingMarkTimers.delete(chatId)
    getDefaultStore().set(chatAwaitingNextDrainAtom, (prev) => {
      if (prev.has(chatId)) return prev
      const next = new Set(prev)
      next.add(chatId)
      return next
    })
  }, MARK_AWAITING_DELAY_MS)
  pendingMarkTimers.set(chatId, timer)
}

export const clearChatAwaitingNextDrain = (chatId: string): void => {
  if (!chatId) return
  const pending = pendingMarkTimers.get(chatId)
  if (pending) {
    clearTimeout(pending)
    pendingMarkTimers.delete(chatId)
  }
  getDefaultStore().set(chatAwaitingNextDrainAtom, (prev) => {
    if (!prev.has(chatId)) return prev
    const next = new Set(prev)
    next.delete(chatId)
    return next
  })
}

/**
 * Chats whose queue still has work between turns — items queued or awaiting
 * the next drain. Read by the per-chat turn status (`draining`).
 */
export const queueBusyChatsAtom = atom<Set<string>>((get) => {
  const queued = get(queuedMessagesAtom)
  const busy = new Set(get(chatAwaitingNextDrainAtom))
  for (const [chatId, list] of Object.entries(queued)) {
    if (list.length > 0) busy.add(chatId)
  }
  return busy
})

export const useIsChatAwaitingNextDrain = (chatId: string | undefined): boolean => {
  const selector = useCallback((state: Set<string>) => (chatId ? state.has(chatId) : false), [chatId])
  const selectedAtom = useMemo(() => selectAtom(chatAwaitingNextDrainAtom, selector), [selector])
  return useAtomValue(selectedAtom)
}

// Returns true when the chat's queue has been empty AND not draining for at
// least one panel-exit-animation cycle (~220ms). Consumers can use this to
// hold their own enter animations until the queued bubble panel has finished
// collapsing and the bottom-anchor scroll has settled.
export const useIsChatQueueSettled = (chatId: string | undefined): boolean => {
  const isNonEmpty = useIsChatQueueNonEmpty(chatId)
  const isAwaiting = useIsChatAwaitingNextDrain(chatId)
  const isBusy = isNonEmpty || isAwaiting

  const [isSettled, setIsSettled] = useState(!isBusy)

  useEffect(() => {
    if (isBusy) {
      setIsSettled(false)
      return
    }
    const id = setTimeout(() => setIsSettled(true), 220)
    return () => clearTimeout(id)
  }, [isBusy])

  return isSettled
}

export const useIsQueueFull = (chatId: string | undefined): boolean => {
  const selector = useCallback(
    (state: QueuedMessagesMap) => (chatId ? (state[chatId]?.length ?? 0) >= MAX_QUEUED_MESSAGES_PER_CHAT : false),
    [chatId],
  )
  const selectedAtom = useMemo(() => selectAtom(queuedMessagesAtom, selector), [selector])
  return useAtomValue(selectedAtom)
}

// React-flavored wrapper around the cancelling flag toggle. All other queue
// mutations are driven from the SSE handler in useSubmitMessage via the
// module-level helpers — only this one is exposed as a hook because it's
// called from a UI event handler (cancel button) that wants stable identity.
//
// Returns true when the flag actually flipped (i.e. the caller is the first
// to set this value). Rapid double-clicks before React re-renders are
// serialized through the atom setter, so the second call sees the freshly
// updated state and returns false — letting the caller bail before
// firing a duplicate DELETE.
export const useSetQueueCancelling = (chatId: string | undefined): ((localId: string, value: boolean) => boolean) => {
  const setQueued = useSetAtom(queuedMessagesAtom)
  return useCallback(
    (localId: string, value: boolean) => {
      if (!chatId) return false
      let changed = false
      setQueued((prev) => {
        const list = prev[chatId]
        if (!list) return prev
        const idx = list.findIndex((m) => m.localId === localId)
        if (idx === -1) return prev
        if (list[idx].cancelling === value) return prev
        changed = true
        const next = [...list]
        next[idx] = { ...next[idx], cancelling: value }
        return { ...prev, [chatId]: next }
      })
      return changed
    },
    [chatId, setQueued],
  )
}
